import React, { useEffect, useRef, useState } from "react";
import { ResponsiveContainer, Cell, BarChart, Bar, XAxis, YAxis, LabelList } from "recharts";
import axiosInstance from "../../../Services/axiosInstance";
import { People } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import { closeLoading, showLoading } from "../../../General/Loader";
import { PieChart, Pie, Tooltip, Legend } from "recharts";

const Colors = ["#fc4e15", "#272324", "#adadad", "#00C49F"];

const AdminDashboard: React.FC = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);
  const [userPieData, setUserPieData] = useState([
    { name: "Active Users", value: 0 },
    { name: "Inactive Users", value: 0 },
  ]);

  const [productChartData, setProductChartData] = useState<any>([]);
  const [filteredProductChartData, setFilteredProductChartData] = useState<any>([]);
  const [orderChartData, setOrderChartData] = useState<any>([]);
  const [filteredOrderChartData, setFilteredOrderChartData] = useState<any>([]);
  const hasFetchedUserData = useRef(false);

  const [visibleItems, setVisibleItems]: any = useState({
    set1: true,
    set2: true,
    set3: true,
  });

  const [visibleOrderItems, setVisibleOrderItems]: any = useState({
    totalOrders: true,
    pendingOrders: true,
    successOrders: true,
  });

  const Products = [
    { name: "set1", label: "Active Products", color: "#fc4e15", key: "activeProducts" },
    { name: "set2", label: "Inactive Products", color: "#272324", key: "inactiveProducts" },
    { name: "set3", label: "Total Products", color: "#adadad", key: "totalProducts" },
  ];

  const OrderTypes = [
    { name: "totalOrders", label: "Total Orders", color: "#fc4e15" },
    { name: "pendingOrders", label: "Pending Orders", color: "#272324" },
    { name: "successOrders", label: "Success Orders", color: "#adadad" },
  ];

  // Function to handle product checkbox changes
  const handleProductCheckboxChange = (itemName: string) => {
    setVisibleItems((prev: any) => {
      const newState = { ...prev, [itemName]: !prev[itemName] };

      // Check if all would be unchecked
      const checkedCount = Object.values(newState).filter(Boolean).length;
      if (checkedCount === 0) {
        // If all would be unchecked, don't allow and return previous state
        return prev;
      }

      return newState;
    });
  };

  // Function to handle order checkbox changes
  const handleOrderCheckboxChange = (itemName: string) => {
    setVisibleOrderItems((prev: any) => {
      const newState = { ...prev, [itemName]: !prev[itemName] };

      // Check if all would be unchecked
      const checkedCount = Object.values(newState).filter(Boolean).length;
      if (checkedCount === 0) {
        // If all would be unchecked, don't allow and return previous state
        return prev;
      }

      return newState;
    });
  };

  useEffect(() => {
    if (hasFetchedUserData.current) return;
    hasFetchedUserData.current = true;

    const fetchUserAndProducts = async () => {
      showLoading(3000);
      try {
        const [userRes, productRes, ordersRes]: any = await Promise.all([
          axiosInstance.get("/user"),
          axiosInstance.get("/products?page=1&per_page=1"),
          axiosInstance.get("/getorder"),
        ]);

        const totalUsers = Number(userRes.data?.total);
        const activeUsers = Number(userRes.data?.active_user);
        const inactiveUsers = Number(userRes.data?.inactive_user);

        setTotalUsers(totalUsers);
        setActiveUsers(activeUsers);
        setInactiveUsers(inactiveUsers);

        setUserPieData([
          { name: "Active Users", value: activeUsers },
          { name: "Inactive Users", value: inactiveUsers },
        ]);

        const productData = [
          { name: "Active Products", value: productRes.data.total_active_product, key: "activeProducts" },
          { name: "Inactive Products", value: productRes.data.total_inactive_product, key: "inactiveProducts" },
          { name: "Total Products", value: productRes.data.total_products, key: "totalProducts" },
        ];

        setProductChartData(productData);
        setFilteredProductChartData(productData);

        const orderData = [
          { name: "Total Orders", value: ordersRes.data.total, key: "totalOrders" },
          { name: "Pending Orders", value: ordersRes.data.pending_order, key: "pendingOrders" },
          { name: "Success Orders", value: ordersRes.data.success_order, key: "successOrders" },
        ];

        setOrderChartData(orderData);
        setFilteredOrderChartData(orderData);

      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        closeLoading();
      }
    };

    fetchUserAndProducts();
  }, []);

  // Filter product chart data based on checkbox selection
  useEffect(() => {
    if (productChartData.length > 0) {
      const filteredData = productChartData.filter((item: any) => {
        const productType = Products.find(p => p.key === item.key);
        return productType ? visibleItems[productType.name] : true;
      });
      setFilteredProductChartData(filteredData);
    }
  }, [visibleItems, productChartData]);

  // Filter order chart data based on checkbox selection
  useEffect(() => {
    if (orderChartData.length > 0) {
      const filteredData = orderChartData.filter((item: any) =>
        visibleOrderItems[item.key]
      );
      setFilteredOrderChartData(filteredData);
    }
  }, [visibleOrderItems, orderChartData]);

  // Custom label component for bar chart
  const CustomBarLabel = (props: any) => {
    const { x, y, width, value } = props;
    return (
      <text
        x={x + width + 5}
        y={y + 15}
        fill="#333"
        fontSize={12}
        fontWeight="bold"
        textAnchor="start"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="w-full">
      <div
        className="flex flex-col lg:flex-row gap-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
        style={{ height: "calc(100vh - 140px)", maxHeight: "calc(100vh - 140px)" }}
      >
        <div className="flex flex-col gap-4 w-full lg:w-3/4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Product Overview</h2>

            <div className="w-full h-[300px]">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={filteredProductChartData}
                  margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#333", fontSize: 12 }}
                    axisLine={{ stroke: "#ccc" }}
                    tickLine={{ stroke: "#ccc" }}
                  />
                  <YAxis
                    tick={{ fill: "#333", fontSize: 12 }}
                    axisLine={{ stroke: "#ccc" }}
                    tickLine={{ stroke: "#ccc" }}
                  />
                  <Bar
                    dataKey="value"
                    barSize={30}
                    radius={[10, 10, 10, 10]}
                  >
                    {filteredProductChartData.map((entry: any, index: number) => (
                      <Cell
                        key={entry.name}
                        fill={Products.find(type => type.key === entry.key)?.color || Colors[index % Colors.length]}
                      />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      fill="#333"
                      fontSize={12}
                      fontWeight="bold"
                      formatter={(value: number) => value > 0 ? value : ''}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 flex flex gap-4 justify-center">
              {Products.map((item: any, index: any) => (
                <label key={index} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleItems[item.name]}
                    onChange={() => handleProductCheckboxChange(item.name)}
                  />
                  <span>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Order Overview
            </h2>

            <div className="w-full h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={filteredOrderChartData}
                  layout="vertical"
                  margin={{ top: 10, right: 50, left: 40, bottom: 10 }}
                >
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: "#333", fontSize: 12 }}
                    axisLine={{ stroke: "#ccc" }}
                    tickLine={{ stroke: "#ccc" }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fill: "#333", fontSize: 12 }}
                    axisLine={{ stroke: "#ccc" }}
                    tickLine={{ stroke: "#ccc" }}
                  />
                  <Bar
                    dataKey="value"
                    radius={[10, 10, 10, 10]}
                  >
                    {filteredOrderChartData.map((entry: any, index: number) => (
                      <Cell
                        key={entry.name}
                        fill={OrderTypes.find(type => type.name === entry.key)?.color || Colors[index % Colors.length]}
                      />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="right"
                      fill="#333"
                      fontSize={12}
                      fontWeight="bold"
                      formatter={(value: number) => value > 0 ? value : ''}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 flex gap-4 justify-center">
              {OrderTypes.map((item: any, index: any) => (
                <label key={index} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleOrderItems[item.name]}
                    onChange={() => handleOrderCheckboxChange(item.name)}
                  />
                  <span>
                    {item.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/4 flex flex-col gap-4">
          <div className="bg-white rounded-xl shadow-md p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">User Statistics</h2>
            <div className="">
              <div className="w-full flex flex-col gap-3">
                {[
                  { icon: <People className="w-5 h-5 text-gray-800" />, label: "Total Users", value: totalUsers },
                  { icon: <PersonIcon className="w-5 h-5 text-gray-800" />, label: "Active Users", value: activeUsers },
                  { icon: <NoAccountsIcon className="w-5 h-5 text-gray-800" />, label: "Inactive Users", value: inactiveUsers },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span className="text-sm font-medium text-gray-700">{item.label}</span>
                    </div>
                    <span className="text-base font-bold text-gray-800">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="w-full flex justify-center">
                <PieChart width={150} height={220}>
                  <Pie
                    data={userPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {userPieData.map((entry, index) => (
                      <Cell key={index} fill={Colors[index % Colors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;